// [COPIAR CÓDIGO]
//
/* GS://PROTOCOLO
 * Arquivo: src/pages/finance/FinancialDashboardPage.js
 * Dossiê de Projeto e Manual de Colaboração: Ecossistema GoldenSisters
 * Versão: 12.1 – UX: Cards de Insights Clicáveis (Atalhos)
 * Impacto da Versão: [src/pages/finance/FinancialDashboardPage.js]
 * Data: 13/01/2026
 * Responsável: Gemini (conforme Protocolo)
 *
 * Mudanças desta versão:
 * - ADICIONADO: Propriedade `onClick` e estilos de cursor/hover nos cards "Aniversariantes" e "Clientes Ativos".
 * - ROTA: Aniversariantes -> /customers
 * - ROTA: Clientes Ativos -> /orders
 */

import React, { useState, useEffect } from 'react';
import { db } from '../../firebase/config'; 
import { useNavigate } from 'react-router-dom';
import { 
  DollarSign, 
  ShoppingCart, 
  TrendingUp, 
  Calendar as CalendarIcon, 
  Star,
  Trophy,
  AlertCircle,
  Activity,
  Wallet,
  ArrowUpCircle,
  Users,
  Gift,
  Clock
} from 'lucide-react';
import { 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  AreaChart,
  Area
} from 'recharts';
import { collection, getDocs } from 'firebase/firestore';
import financialService from '../../services/financialService';

// Componente de Card KPI Genérico
const KpiCard = ({ title, value, subtext, icon: Icon, colorClass, borderClass, onClick }) => (
  <div 
    onClick={onClick}
    className={`bg-white rounded-lg p-6 shadow-sm border-l-4 ${borderClass} flex flex-col justify-between h-32 relative overflow-hidden transition-transform hover:scale-[1.02] cursor-pointer`}
  >
    <div className="flex justify-between items-start z-10">
      <div>
        <p className="text-gray-500 text-xs font-bold uppercase tracking-wide">{title}</p>
        <h3 className="text-2xl font-extrabold text-gray-800 mt-1">{value}</h3>
      </div>
      <div className={`p-2 rounded-full ${colorClass} bg-opacity-10 text-opacity-100`}>
        <Icon className={`w-6 h-6 ${colorClass.replace('bg-', 'text-')}`} />
      </div>
    </div>
    {subtext && <p className="text-xs text-gray-400 mt-2 z-10 font-medium">{subtext}</p>}
    <div className="absolute -bottom-4 -right-4 opacity-5 transform rotate-12">
      <Icon className="w-24 h-24" />
    </div>
  </div>
);

const FinancialDashboardPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  
  // Estado para Dados de Vendas (KPIs antigos)
  const [salesData, setSalesData] = useState({
    totalRevenue: 0, 
    salesCount: 0, 
    productCount: 0, 
    averageTicket: 0, 
    pendingOrdersCount: 0,
    chartData: [],
    topProducts: [],
    recentCustomers: [] // Novo: Clientes Recentes
  });

  // Estado para Dados do Ledger (Financeiro Real)
  const [ledgerData, setLedgerData] = useState({
      income: 0,
      expense: 0,
      balance: 0
  });

  // Estado para Auditoria de Pontos (Passivo)
  const [pointsAudit, setPointsAudit] = useState({
      liability: 0, // Passivo Total
      issued: 0     // Emitido Histórico
  });

  // Estado para Aniversariantes
  const [birthdays, setBirthdays] = useState([]);

  // Dates
  const getBrazilDateString = () => {
    const offsetBrazil = 3 * 60 * 60 * 1000; 
    const now = new Date(Date.now() - offsetBrazil);
    return now.toISOString().split('T')[0];
  };

  const getBrazilFirstDayString = () => {
    const offsetBrazil = 3 * 60 * 60 * 1000;
    const now = new Date(Date.now() - offsetBrazil);
    return now.toISOString().slice(0, 8) + '01'; 
  };
  
  const [startDate, setStartDate] = useState(getBrazilFirstDayString());
  const [endDate, setEndDate] = useState(getBrazilDateString());

  // 1. Carregar Dados do Ledger (Caixa Real)
  const loadLedger = async () => {
      try {
          const balance = await financialService.getBalance({ startDate, endDate }); 
          setLedgerData(balance);
      } catch (error) {
          console.error("Erro ao carregar Ledger:", error);
      }
  };

  // 2. Carregar Dados de Vendas (Filtrados por Data) + Clientes Recentes
  const loadSalesData = async () => {
    setLoading(true);
    try {
      const [salesSnap, ordersSnap] = await Promise.all([
        getDocs(collection(db, 'sales')),
        getDocs(collection(db, 'orders'))
      ]);

      let revenue = 0;
      let count = 0;
      let productQty = 0;
      let pendingGlobal = 0;
      
      const salesByDate = {};
      const productsMap = {};
      const customersMap = {}; // Mapa para identificar últimas compras

      const [sy, sm, sd] = startDate.split('-').map(Number);
      const startTimestamp = Date.UTC(sy, sm - 1, sd, 3, 0, 0, 0); 

      const [ey, em, ed] = endDate.split('-').map(Number);
      const endTimestamp = Date.UTC(ey, em - 1, ed + 1, 2, 59, 59, 999); 

      const closedStatuses = [
          'concluido', 'concluído', 'cancelado', 'cancelled',
          'entregue', 'refunded', 'reembolsado', 'recusado', 'estornado'
      ];

      const processDoc = (doc, source) => {
        const d = doc.data();
        let orderMillis = 0;
        let orderDateObj = null;
        
        if (d.createdAt && typeof d.createdAt.toMillis === 'function') {
            orderMillis = d.createdAt.toMillis();
            orderDateObj = d.createdAt.toDate();
        } else if (d.date && typeof d.date.toMillis === 'function') {
            orderMillis = d.date.toMillis();
            orderDateObj = d.date.toDate();
        } else if (d.date && d.date instanceof Date) {
            orderMillis = d.date.getTime();
            orderDateObj = d.date;
        } else {
            orderDateObj = new Date();
            orderMillis = orderDateObj.getTime();
        }

        const st = (d.status || '').toLowerCase().trim();
        
        // Contagem de Pendentes
        if (source === 'order' && st && !closedStatuses.includes(st)) {
            pendingGlobal++;
        }

        // Filtro de Data para Financeiro
        if (orderMillis >= startTimestamp && orderMillis <= endTimestamp) {
            if (st.includes('cancel') || st.includes('recusado') || st.includes('refund')) return;

            let val = Number(d.totalValue || d.total); 
            if (!val && d.items) {
                if (d.subtotal) val = Number(d.subtotal);
                else if (d.items.length > 0) val = d.items.reduce((acc, i) => acc + (Number(i.price || i.unitPrice || 0)*Number(i.quantity)), 0);
            }

            revenue += val || 0;
            count++;

            const brazilDateKey = new Intl.DateTimeFormat('en-CA', { 
                timeZone: 'America/Sao_Paulo',
                year: 'numeric', month: '2-digit', day: '2-digit'
            }).format(orderDateObj); 

            salesByDate[brazilDateKey] = (salesByDate[brazilDateKey] || 0) + (val || 0);

            // Produtos
            if (d.items && Array.isArray(d.items)) {
                d.items.forEach(item => {
                    const pid = item.productId || item.name;
                    const pqty = Number(item.quantity || 0);
                    productQty += pqty;

                    if (!productsMap[pid]) {
                        productsMap[pid] = { 
                            name: item.name || item.title || 'Item s/ nome', 
                            code: item.sku || 'SKU', 
                            quantity: 0 
                        };
                    }
                    productsMap[pid].quantity += pqty;
                });
            }

            // --- Lógica de Clientes Recentes (Dentro do Filtro de Data) ---
            // Tenta identificar o nome do cliente
            const customerName = d.clientName || d.customerName || (d.user ? d.user.displayName : null) || (d.customer ? d.customer.name : null);
            const customerEmail = d.email || (d.user ? d.user.email : null) || (d.customer ? d.customer.email : null) || customerName; // Email serve como ID único se disponível

            if (customerName && customerEmail) {
              if (!customersMap[customerEmail]) {
                customersMap[customerEmail] = {
                  name: customerName,
                  lastDate: orderMillis,
                  totalSpent: 0,
                  count: 0
                };
              }
              // Atualiza se encontrou uma data mais recente (embora este loop dependa da ordem, garantimos comparando)
              if (orderMillis > customersMap[customerEmail].lastDate) {
                 customersMap[customerEmail].lastDate = orderMillis;
              }
              customersMap[customerEmail].totalSpent += (val || 0);
              customersMap[customerEmail].count += 1;
            }
        }
      };

      salesSnap.forEach(doc => processDoc(doc, 'sale'));
      ordersSnap.forEach(doc => processDoc(doc, 'order'));

      // Gráficos
      const chartData = Object.keys(salesByDate).sort().map(key => {
          const [, m, d] = key.split('-');
          return {
              date: `${d}/${m}`,
              total: salesByDate[key]
          };
      });

      // Top Produtos
      const topProducts = Object.values(productsMap)
          .sort((a, b) => b.quantity - a.quantity)
          .slice(0, 5);

      // Top Clientes Recentes (Transforma Map em Array e Ordena por Data)
      const recentCustomersList = Object.values(customersMap)
          .sort((a, b) => b.lastDate - a.lastDate)
          .slice(0, 5);

      setSalesData({
          totalRevenue: revenue,
          salesCount: count,
          productCount: productQty,
          averageTicket: count > 0 ? revenue / count : 0,
          pendingOrdersCount: pendingGlobal,
          chartData,
          topProducts,
          recentCustomers: recentCustomersList
      });

    } catch (error) {
      console.error("Erro no motor de vendas:", error);
    } finally {
      setLoading(false);
    }
  };

  // 3. Auditoria de Pontos (Passivo) & 4. Aniversariantes
  const loadUsersAndAudit = async () => {
      try {
          const usersSnap = await getDocs(collection(db, 'users'));
          
          let liabilityTotal = 0;
          let birthdayList = [];
          const currentMonth = new Date().getMonth(); // 0-11
          const currentDay = new Date().getDate();

          usersSnap.forEach(doc => {
              const u = doc.data();
              
              // Pontos
              if (u.availablePoints && typeof u.availablePoints === 'number') {
                  liabilityTotal += u.availablePoints;
              }

              // Aniversário
              if (u.birthDate) {
                let bDate = null;
                // Parse Data
                if (typeof u.birthDate.toDate === 'function') {
                  bDate = u.birthDate.toDate();
                } else if (u.birthDate instanceof Date) {
                  bDate = u.birthDate;
                } else if (typeof u.birthDate === 'string') {
                  // Tenta parse DD/MM ou YYYY-MM-DD
                  const parts = u.birthDate.match(/(\d+)/g);
                  if (parts && parts.length >= 2) {
                    // Assume DD/MM como padrão BR se string curta
                    if (u.birthDate.includes('/')) {
                       bDate = new Date();
                       bDate.setDate(parseInt(parts[0]));
                       bDate.setMonth(parseInt(parts[1]) - 1);
                    } else {
                       bDate = new Date(u.birthDate);
                    }
                  }
                }

                if (bDate && !isNaN(bDate.getTime())) {
                  if (bDate.getMonth() === currentMonth) {
                     birthdayList.push({
                       name: u.name || u.displayName || 'Cliente',
                       day: bDate.getDate(),
                       isToday: bDate.getDate() === currentDay
                     });
                  }
                }
              }
          });

          // Busca Pontos Emitidos (Vendas)
          const allSalesSnap = await getDocs(collection(db, 'sales'));
          let issuedTotal = 0;
          allSalesSnap.forEach(doc => {
              const s = doc.data();
              if (s.pointsEarned && typeof s.pointsEarned === 'number') {
                  issuedTotal += s.pointsEarned;
              }
          });

          setPointsAudit({ liability: liabilityTotal, issued: issuedTotal });
          setBirthdays(birthdayList.sort((a, b) => a.day - b.day));

      } catch (error) {
          console.error("Erro na auditoria/users:", error);
      }
  };

  useEffect(() => {
    loadUsersAndAudit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadSalesData();
    loadLedger();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate]);

  const fmtMoney = (v) => (v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  const fmtPoints = (v) => (v || 0).toLocaleString('pt-BR') + ' GP';

  return (
    <div className="p-6 bg-gray-50 min-h-screen font-sans">
      
      {/* HEADER E FILTROS */}
      <div className="flex flex-col md:flex-row justify-between items-center mb-6 bg-white p-4 rounded-lg shadow-sm border border-gray-100">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Activity className="text-brand-gold" /> Painel de Controle
          </h1>
          <p className="text-sm text-gray-500">Visão unificada 360° (Financeiro + Vendas)</p>
        </div>
        <div className="flex items-center gap-4 mt-4 md:mt-0">
          <div className="flex items-center bg-gray-100 rounded-md px-3 py-2 border border-gray-200">
            <CalendarIcon className="w-4 h-4 text-gray-500 mr-2" />
            <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="bg-transparent border-none text-sm text-gray-700 focus:ring-0 p-0 outline-none" />
            <span className="mx-2 text-gray-400">até</span>
            <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="bg-transparent border-none text-sm text-gray-700 focus:ring-0 p-0 outline-none" />
          </div>
          <button onClick={() => { loadSalesData(); loadLedger(); loadUsersAndAudit(); }} className="bg-brand-black text-white font-bold py-2 px-6 rounded-md hover:bg-gray-800 transition-colors shadow-sm text-sm">
            Atualizar
          </button>
        </div>
      </div>

      {/* SEÇÃO 1: VISÃO PATRIMONIAL E RESULTADOS */}
      <h2 className="text-lg font-bold text-gray-700 mb-4 flex items-center gap-2">
        <Wallet className="w-5 h-5 text-gray-400" /> Visão Geral
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        
        {/* CARD 1: CAIXA REAL */}
        <KpiCard 
            title="Caixa Livre (Real)" 
            value={fmtMoney(ledgerData.balance)} 
            subtext={`Entradas: ${fmtMoney(ledgerData.income)} | Saídas: ${fmtMoney(ledgerData.expense)}`}
            icon={DollarSign} 
            colorClass={ledgerData.balance >= 0 ? "bg-green-500 text-green-600" : "bg-red-500 text-red-600"} 
            borderClass={ledgerData.balance >= 0 ? "border-green-500" : "border-red-500"} 
            onClick={() => navigate('/financeiro')}
        />

        {/* CARD 2: PASSIVO PONTOS */}
        <KpiCard 
            title="Passivo GoldenPoints" 
            value={fmtPoints(pointsAudit.liability)} 
            subtext={`Dívida Ativa com Clientes (Emitido: ${fmtPoints(pointsAudit.issued)})`} 
            icon={Star} 
            colorClass="bg-yellow-500 text-yellow-600" 
            borderClass="border-yellow-500" 
            onClick={() => navigate('/customers')}
        />

        {/* CARD 3: TICKET MÉDIO */}
        <KpiCard 
            title="Ticket Médio" 
            value={loading ? '...' : fmtMoney(salesData.averageTicket)} 
            subtext="Média por Pedido (Período)" 
            icon={ArrowUpCircle} 
            colorClass="bg-purple-500 text-purple-600" 
            borderClass="border-purple-500" 
            onClick={() => navigate('/orders')} 
        />

        {/* CARD 4: VENDAS TOTAIS */}
        <KpiCard 
            title="Vendas (Período)" 
            value={loading ? '...' : fmtMoney(salesData.totalRevenue)} 
            subtext={`${salesData.salesCount} pedidos realizados`} 
            icon={TrendingUp} 
            colorClass="bg-blue-500 text-blue-600" 
            borderClass="border-blue-500" 
            onClick={() => navigate('/orders')}
        />
      </div>

      {/* SEÇÃO 2: INSIGHTS DE CLIENTES (NOVO) */}
      <h2 className="text-lg font-bold text-gray-700 mb-4 flex items-center gap-2">
        <Users className="w-5 h-5 text-gray-400" /> Insights de Clientes
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        
        {/* Aniversariantes */}
        <div 
          onClick={() => navigate('/customers')}
          className="bg-white p-6 rounded-lg shadow-sm border border-gray-100 h-64 overflow-y-auto cursor-pointer hover:shadow-md transition-all"
        >
           <h3 className="text-sm font-bold text-gray-600 mb-4 flex items-center uppercase tracking-wider sticky top-0 bg-white pb-2 border-b border-gray-50">
             <Gift className="w-4 h-4 text-pink-500 mr-2" /> Aniversariantes do Mês
           </h3>
           <div className="space-y-3">
             {birthdays.length > 0 ? (
               birthdays.map((b, idx) => (
                 <div key={idx} className={`flex justify-between items-center p-2 rounded-md ${b.isToday ? 'bg-pink-50 border border-pink-100' : 'hover:bg-gray-50'}`}>
                    <div className="flex items-center gap-3">
                       <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-xs ${b.isToday ? 'bg-pink-500 text-white' : 'bg-gray-100 text-gray-600'}`}>
                         {b.day}
                       </div>
                       <div>
                          <p className={`text-sm font-medium ${b.isToday ? 'text-pink-700' : 'text-gray-700'}`}>{b.name}</p>
                          {b.isToday && <p className="text-[10px] text-pink-500 font-bold uppercase">Hoje!</p>}
                       </div>
                    </div>
                    {b.isToday && <Gift className="w-4 h-4 text-pink-500" />}
                 </div>
               ))
             ) : (
               <p className="text-xs text-gray-400 text-center py-10">Nenhum aniversariante este mês.</p>
             )}
           </div>
        </div>

        {/* Clientes Recentes */}
        <div 
          onClick={() => navigate('/orders')}
          className="bg-white p-6 rounded-lg shadow-sm border border-gray-100 h-64 overflow-y-auto cursor-pointer hover:shadow-md transition-all"
        >
           <h3 className="text-sm font-bold text-gray-600 mb-4 flex items-center uppercase tracking-wider sticky top-0 bg-white pb-2 border-b border-gray-50">
             <Clock className="w-4 h-4 text-blue-500 mr-2" /> Clientes Ativos (Recente)
           </h3>
           <div className="space-y-3">
             {salesData.recentCustomers.length > 0 ? (
               salesData.recentCustomers.map((c, idx) => (
                 <div key={idx} className="flex justify-between items-center p-2 hover:bg-gray-50 rounded-md">
                    <div className="flex items-center gap-3">
                       <div className="w-8 h-8 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center font-bold text-xs uppercase">
                         {c.name.substring(0,2)}
                       </div>
                       <div>
                          <p className="text-sm font-medium text-gray-700 line-clamp-1">{c.name}</p>
                          <p className="text-[10px] text-gray-400">
                             Última: {new Date(c.lastDate).toLocaleDateString('pt-BR')}
                          </p>
                       </div>
                    </div>
                    <div className="text-right">
                       <p className="text-sm font-bold text-gray-800">{fmtMoney(c.totalSpent)}</p>
                       <p className="text-[10px] text-gray-400">{c.count} pedido(s)</p>
                    </div>
                 </div>
               ))
             ) : (
               <p className="text-xs text-gray-400 text-center py-10">Nenhuma atividade recente encontrada.</p>
             )}
           </div>
        </div>
      </div>

      {/* SEÇÃO 3: GRÁFICOS E TOP PRODUTOS */}
      <h2 className="text-lg font-bold text-gray-700 mb-4 flex items-center gap-2">
        <ShoppingCart className="w-5 h-5 text-gray-400" /> Performance de Vendas
      </h2>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100">
            <h3 className="text-sm font-bold text-gray-600 mb-6 uppercase tracking-wider">Evolução Diária</h3>
            <div className="h-80 w-full">
              {loading ? (
                <div className="flex h-full items-center justify-center text-gray-400">Carregando dados...</div>
              ) : salesData.chartData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={salesData.chartData}>
                    <defs>
                      <linearGradient id="colorTotal" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.2}/>
                        <stop offset="95%" stopColor="#3B82F6" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F3F4F6" />
                    <XAxis dataKey="date" axisLine={false} tickLine={false} tick={{fill: '#9CA3AF', fontSize: 12}} dy={10} />
                    <YAxis axisLine={false} tickLine={false} tick={{fill: '#9CA3AF', fontSize: 12}} tickFormatter={(val) => `R$${val}`} />
                    <Tooltip contentStyle={{borderRadius: '8px', border: 'none', boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)'}} formatter={(val) => [fmtMoney(val), 'Venda']} />
                    <Area type="monotone" dataKey="total" stroke="#3B82F6" strokeWidth={3} fillOpacity={1} fill="url(#colorTotal)" />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex h-full items-center justify-center text-gray-400 bg-gray-50 rounded-lg border border-dashed border-gray-200">
                    <p>Sem vendas no período selecionado.</p>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="space-y-6">
          {/* Card Pedidos em Aberto */}
          <div 
            onClick={() => navigate('/orders')}
            className={`bg-white p-6 rounded-lg shadow-sm border-l-4 ${salesData.pendingOrdersCount > 0 ? 'border-red-500' : 'border-green-500'} flex items-center justify-between cursor-pointer hover:shadow-md transition-all`}
          >
              <div>
                <p className="text-gray-500 text-xs font-bold uppercase flex items-center gap-1">
                    <AlertCircle className={`w-3 h-3 ${salesData.pendingOrdersCount > 0 ? 'text-red-500' : 'text-green-500'}`} /> 
                    Status Operacional
                </p>
                <h3 className="text-lg font-bold text-gray-800 mt-1">Pedidos em Aberto</h3>
                <span className="text-xs text-gray-400">Clique para gerenciar fila</span>
              </div>
              <div className="text-right">
                <span className={`text-4xl font-extrabold ${salesData.pendingOrdersCount > 0 ? 'text-red-600' : 'text-green-600'}`}>
                    {salesData.pendingOrdersCount}
                </span>
              </div>
          </div>

          {/* Top Produtos */}
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100">
            <h3 className="text-sm font-bold text-gray-600 mb-4 flex items-center uppercase tracking-wider">
              <Trophy className="w-4 h-4 text-brand-gold mr-2" /> Top Produtos
            </h3>
            <div className="space-y-4">
              {salesData.topProducts.length > 0 ? (
                salesData.topProducts.map((prod, idx) => (
                  <div key={idx} className="flex justify-between items-center border-b border-gray-50 last:border-0 pb-2 last:pb-0">
                    <div className="flex items-center gap-3">
                        <span className={`font-bold w-6 h-6 flex items-center justify-center rounded-full text-xs flex-shrink-0 ${idx === 0 ? 'bg-yellow-100 text-yellow-700' : 'bg-gray-100 text-gray-600'}`}>
                          {idx + 1}
                        </span>
                        <div>
                          {prod.code && <p className="text-[10px] text-gray-400 font-mono uppercase">{prod.code}</p>}
                          <p className="text-sm text-gray-700 font-medium leading-tight max-w-[140px] truncate" title={prod.name}>
                            {prod.name}
                          </p>
                        </div>
                    </div>
                    <span className="font-bold text-brand-black bg-gray-50 px-2 py-1 rounded text-xs whitespace-nowrap border border-gray-100">
                        {prod.quantity} un
                    </span>
                  </div>
                ))
              ) : (
                <p className="text-xs text-gray-400 text-center py-4">Nenhuma venda registrada.</p>
              )}
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default FinancialDashboardPage;